import React from 'react';
import { AthleteProfile } from '../types';
import { ProTipCard } from './ProTipCard';
import { SmartWarmup } from './SmartWarmup';
import { Flame, Sparkles, Activity, ShieldCheck, Zap } from 'lucide-react';

interface WarmupViewProps {
  user: AthleteProfile;
  onEarnXp?: (amount: number, source: string) => void;
  onOpenStatLog?: () => void;
  onNavigateToAnalytics?: () => void;
}

export const WarmupView: React.FC<WarmupViewProps> = ({
  user,
  onEarnXp,
  onOpenStatLog,
  onNavigateToAnalytics,
}) => {
  return (
    <div className="space-y-8 pb-12 animate-fadeIn max-w-7xl mx-auto px-4 sm:px-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-950 p-6 md:p-8 rounded-[2.5rem] border border-lime-400/30 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-48 h-48 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 bg-lime-400/20 text-lime-300 font-mono text-xs font-black uppercase rounded-full border border-lime-400/40 flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 fill-lime-400 text-lime-400" />
                PRE-GAME ACTIVATION
              </span>
              <span className="px-2.5 py-0.5 bg-indigo-900/80 text-indigo-200 text-xs font-bold rounded-full border border-white/10">
                AI Guided
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black italic uppercase text-white tracking-wide">
              Warmups & AI Coach Pro-Tips
            </h1>
            <p className="text-indigo-200/90 text-sm max-w-2xl font-medium leading-relaxed">
              Activate targeted muscle groups, reduce injury risks with dynamic 3-minute sport routines, and review real-time AI performance coaching insights.
            </p>
          </div>

          <div className="flex items-center space-x-3 self-start sm:self-auto shrink-0">
            <div className="p-4 bg-indigo-900/70 border border-white/10 rounded-2xl flex items-center space-x-3 text-white shadow-inner">
              <div className="p-2.5 bg-lime-400 text-black rounded-xl font-black">
                <Activity className="w-6 h-6 stroke-[2.5]" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-mono text-indigo-300 font-bold block">
                  Daily Warmup Status
                </span>
                <span className="text-sm font-black italic text-lime-400">
                  Ready to Activate ⚡
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 1. AI COACH PRO-TIP CARD */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-lime-400" />
            <h2 className="text-lg font-black italic uppercase text-white tracking-wide">
              AI Coach Insights & Pro-Tips
            </h2>
          </div>
          <span className="text-xs text-indigo-300 font-mono font-semibold">
            Updated based on recent stat history
          </span>
        </div>

        <ProTipCard
          user={user}
          onNavigateToAnalytics={onNavigateToAnalytics || (() => {})}
        />
      </section>

      {/* 2. 3-MINUTE DYNAMIC WARMUP ENGINE */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Zap className="w-5 h-5 text-amber-400 fill-amber-400" />
            <h2 className="text-lg font-black italic uppercase text-white tracking-wide">
              3-Minute Dynamic Warmup Engine
            </h2>
          </div>
          <span className="text-xs text-lime-400 font-mono font-bold px-2.5 py-0.5 bg-lime-400/10 rounded-full border border-lime-400/30">
            +50 XP Bonus Per Routine
          </span>
        </div>

        <SmartWarmup
          user={user}
          onEarnXp={onEarnXp}
          onOpenStatLog={onOpenStatLog}
        />
      </section>
    </div>
  );
};
